import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Segmented } from 'antd';
import { CloseOutlined, MinusOutlined } from '@ant-design/icons';
import logo from '../logo.svg';
import ConsoleComponent from '../views/console/ConsoleComponent';
import {
  CONSOLE_THEME_OPTIONS,
  getStoredConsoleTheme,
  setStoredConsoleTheme,
} from '../config/consoleThemes';
import './ConsoleOverlay.css';

export default function ConsoleOverlay({ open, onClose }) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [theme, setTheme] = useState(getStoredConsoleTheme);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setMinimized(false);
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }

    setVisible(false);
    const timer = window.setTimeout(() => setMounted(false), 320);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const themeOptions = useMemo(() => CONSOLE_THEME_OPTIONS, []);

  const handleThemeChange = (value) => {
    setTheme(value);
    setStoredConsoleTheme(value);
  };

  if (!mounted) return null;

  return createPortal(
    <>
      {minimized ? (
        <div className={`console-dock console-dock--${theme}`}>
          <button type='button' className='console-dock__restore' onClick={() => setMinimized(false)}>
            Console
          </button>
          <button
            type='button'
            className='console-dock__close'
            onClick={() => onClose?.()}
            aria-label='Close console'
          >
            <CloseOutlined />
          </button>
        </div>
      ) : (
        <div
          className={`console-sheet ${visible ? 'console-sheet--visible' : ''} console-sheet--${theme}`}
          role='dialog'
          aria-label='Console'
        >
          <header className='console-sheet__header'>
            <div className='console-sheet__brand'>
              <img src={logo} alt='' className='console-sheet__logo' />
              <span className='console-sheet__brand-text'>EO4EU</span>
            </div>

            <div className='console-sheet__actions'>
              <Segmented
                size='small'
                className='console-sheet__themes'
                options={themeOptions}
                value={theme}
                onChange={handleThemeChange}
              />
              <Button
                type='text'
                size='small'
                className='console-sheet__icon-btn'
                icon={<MinusOutlined />}
                onClick={() => setMinimized(true)}
                aria-label='Minimize console'
              >
                <span className='console-sheet__btn-label'>Minimize</span>
              </Button>
              <Button
                type='text'
                size='small'
                className='console-sheet__icon-btn'
                icon={<CloseOutlined />}
                onClick={() => onClose?.()}
                aria-label='Close console'
              />
            </div>
          </header>

          <div className={`console-sheet__body console-sheet__body--${theme}`}>
            <ConsoleComponent theme={theme} />
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}
